<template>
  <div class="today-dashboard">
    <!-- Today's Date Header -->
    <div class="today-header">
      <h3>
        <md-icon>today</md-icon>
        {{ formatDate(today) }}
      </h3>
      <div class="live-badge">
        <span class="pulse"></span>
        Live Updates
      </div>
      <div class="refresh-control">
        <md-button class="md-icon-button md-primary md-small" @click="refreshData" :disabled="isRefreshing">
          <md-icon :class="{ 'spinning': isRefreshing }">refresh</md-icon>
        </md-button>
        <span class="last-updated" v-if="lastUpdated">
          Updated: {{ formatTime(lastUpdated) }}
        </span>
      </div>
    </div>

    <!-- Real-time Stats -->
    <div class="metrics-grid">
      <div class="metric-card" @click="filterByStatus('checked_in')">
        <div class="metric-icon">
          <md-icon>login</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(checkedIn) }}</div>
          <div class="metric-label">Checked In</div>
        </div>
      </div>

      <div class="metric-card" @click="filterByStatus('checked_out')">
        <div class="metric-icon">
          <md-icon>logout</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(checkedOut) }}</div>
          <div class="metric-label">Checked Out</div>
        </div>
      </div>

      <div class="metric-card" @click="filterByStatus('pending')">
        <div class="metric-icon">
          <md-icon>pending</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(pendingCheckIn) }}</div>
          <div class="metric-label">Pending Check-in</div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon">
          <md-icon>schedule</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(totalScheduled) }}</div>
          <div class="metric-label">Total Scheduled</div>
        </div>
      </div>
    </div>

    <!-- Check-in/Check-out Activity Feed -->
    <div class="md-layout">
      <div class="md-layout-item md-size-60">
        <md-card>
          <md-card-header>
            <div class="card-title">Today's Activity</div>
            <md-button class="md-icon-button md-primary" @click="clearActivities" v-if="activities.length">
              <md-icon>clear_all</md-icon>
            </md-button>
          </md-card-header>
          <md-card-content>
            <div class="activity-feed" v-if="activities.length">
              <div v-for="activity in displayedActivities" :key="activity.id" class="activity-item" :class="activity.type">
                <div class="activity-time">{{ activity.time }}</div>
                <div class="activity-icon">
                  <md-icon>{{ activity.type === 'checkin' ? 'login' : 'logout' }}</md-icon>
                </div>
                <div class="activity-details">
                  <div class="activity-title">{{ activity.labourer_name }}</div>
                  <div class="activity-subtitle">{{ activity.guidance_number }} - {{ activity.workpoint }}</div>
                </div>
                <div class="activity-status">
                  <badge :type="activity.confirmed ? 'success' : 'warning'">
                    {{ activity.confirmed ? 'Confirmed' : 'Pending' }}
                  </badge>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <md-icon>notifications_none</md-icon>
              <p>No activity yet today</p>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-40">
        <!-- Today's Schedule -->
        <md-card>
          <md-card-header>
            <div class="card-title">Today's Schedule</div>
          </md-card-header>
          <md-card-content>
            <div class="schedule-list" v-if="todaySchedules.length">
              <div v-for="schedule in todaySchedules" :key="schedule.id" class="schedule-item">
                <div class="schedule-time">{{ schedule.shift_time }}</div>
                <div class="schedule-info">
                  <div class="schedule-title">{{ schedule.workpoint }}</div>
                  <div class="schedule-subtitle">{{ schedule.activity }}</div>
                </div>
                <div class="schedule-count">
                  <span class="badge">{{ schedule.labourer_count }} labourers</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-state-small">
              <md-icon>event_busy</md-icon>
              <p>No schedules for today</p>
            </div>
          </md-card-content>
        </md-card>

        <!-- Pending Confirmations -->
        <md-card class="mt-3">
          <md-card-header>
            <div class="card-title">Pending Supervisor Confirmations</div>
            <span class="pending-count" v-if="pendingConfirmations.length">{{ pendingConfirmations.length }}</span>
          </md-card-header>
          <md-card-content>
            <div v-if="pendingConfirmations.length">
              <div v-for="pending in pendingConfirmations" :key="pending.id" class="pending-item">
                <div class="pending-info">
                  <div class="pending-name">{{ pending.labourer_name }}</div>
                  <div class="pending-detail">{{ pending.guidance_number }} - {{ pending.workpoint }}</div>
                  <div class="pending-time" v-if="pending.checkin_time">
                    Checked in at: {{ formatTime(pending.checkin_time) }}
                  </div>
                </div>
<!--                <md-button class="md-success md-simple md-dense" @click="confirmCheck(pending.id)">-->
<!--                  Confirm-->
<!--                </md-button>-->
              </div>
            </div>
            <div v-else class="empty-state-small">
              <md-icon>check_circle</md-icon>
              <p>All caught up! No pending confirmations.</p>
            </div>
          </md-card-content>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Pusher from 'pusher-js';
import Badge from '@/components/Badge.vue';
import formMixin from '@/mixins/form-mixin';

export default {
  name: 'TodayDashboard',
  components: {
    Badge
  },
  mixins: [formMixin],
  props: {
    dateRange: {
      type: Object,
      default: null
    },
    autoRefresh: {
      type: Boolean,
      default: true
    },
    refreshInterval: {
      type: Number,
      default: 30000 // 30 seconds
    }
  },
  data() {
    return {
      pusher: null,
      channel: null,
      today: new Date(),
      checkedIn: 0,
      checkedOut: 0,
      pendingCheckIn: 0,
      totalScheduled: 0,
      activities: [],
      todaySchedules: [],
      pendingConfirmations: [],
      isRefreshing: false,
      lastUpdated: null,
      refreshTimer: null,
      activityLimit: 50
    };
  },
  computed: {
    ...mapGetters('dashboard', ['todayStats', 'isLoading']),

    displayedActivities() {
      return this.activities.slice(0, this.activityLimit);
    }
  },
  created() {
    this.fetchTodayData();
    this.initializePusher();
    if (this.autoRefresh) {
      this.startAutoRefresh();
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh();
    if (this.channel) {
      this.channel.unbind_all();
      this.channel.unsubscribe();
    }
    if (this.pusher) {
      this.pusher.disconnect();
    }
  },
  methods: {
    ...mapActions('dashboard', ['fetchTodayStats']),
    ...mapActions('labourerSchedules', ['confirmSchedule']),

    async fetchTodayData() {
      try {
        this.isRefreshing = true;

        // Use date from props or current date
        const targetDate = this.dateRange?.start || this.today;

        await this.fetchTodayStats({ date: this.formatDateYMD(targetDate) });

        const data = this.todayStats;

        if (data) {
          this.checkedIn = data.checked_in || 0;
          this.checkedOut = data.checked_out || 0;
          this.pendingCheckIn = data.pending_check_in || 0;
          this.totalScheduled = data.total_scheduled || 0;
          this.activities = data.activities || [];
          this.todaySchedules = data.schedules || [];
          this.pendingConfirmations = data.pending_confirmations || [];
          this.lastUpdated = new Date();
        }
      } catch (error) {
        console.error('Error fetching today data:', error);
        this.notifyError('Failed to load today\'s data');
      } finally {
        this.isRefreshing = false;
      }
    },

    refreshData() {
      this.fetchTodayData();
    },

    startAutoRefresh() {
      this.refreshTimer = setInterval(() => {
        this.fetchTodayData();
      }, this.refreshInterval);
    },

    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    initializePusher() {
      // Only initialize if Pusher key is configured
      if (!process.env.VUE_APP_PUSHER_KEY) {
        console.warn('Pusher not configured');
        return;
      }

      this.pusher = new Pusher(process.env.VUE_APP_PUSHER_KEY, {
        cluster: process.env.VUE_APP_PUSHER_CLUSTER || 'mt1',
        encrypted: true
      });

      this.channel = this.pusher.subscribe('checkins');

      this.channel.bind('LabourerCheckedIn', (data) => {
        this.handleCheckIn(data);
      });

      this.channel.bind('LabourerCheckedOut', (data) => {
        this.handleCheckOut(data);
      });

      this.channel.bind('LabourerConfirmed', (data) => {
        this.handleConfirmation(data);
      });
    },

    handleCheckIn(data) {
      this.addActivity('checkin', data.labourer);
      this.checkedIn++;
      this.pendingCheckIn--;
      this.updatePendingConfirmation(data);
    },

    handleCheckOut(data) {
      this.addActivity('checkout', data.labourer);
      this.checkedOut++;
    },

    handleConfirmation(data) {
      // Update the confirmation status in activities
      const activity = this.activities.find(a => a.id === data.activity_id);
      if (activity) {
        activity.confirmed = true;
      }

      // Remove from pending confirmations
      const index = this.pendingConfirmations.findIndex(p => p.id === data.schedule_id);
      if (index !== -1) {
        this.pendingConfirmations.splice(index, 1);
      }

      this.notifySuccess('Attendance confirmed');
    },

    updatePendingConfirmation(data) {
      // Add to pending confirmations if not already there
      const exists = this.pendingConfirmations.some(p => p.id === data.schedule_id);
      if (!exists && data.labourer) {
        this.pendingConfirmations.push({
          id: data.schedule_id,
          labourer_name: data.labourer.name,
          guidance_number: data.labourer.guidance_number,
          workpoint: data.labourer.workpoint,
          checkin_time: data.checkin_time
        });
      }
    },

    addActivity(type, labourer) {
      this.activities.unshift({
        id: Date.now(),
        type: type,
        time: new Date().toLocaleTimeString(),
        labourer_name: labourer.name,
        guidance_number: labourer.guidance_number,
        workpoint: labourer.workpoint,
        confirmed: false
      });

      // Keep only last 50 activities
      while (this.activities.length > 100) {
        this.activities.pop();
      }
    },

    clearActivities() {
      this.activities = [];
    },

    filterByStatus(status) {
      this.$emit('filter-by-status', status);
    },

    async confirmCheck(scheduleId) {
      try {
        await this.confirmSchedule(scheduleId);
        await this.fetchTodayData();
        this.notifySuccess('Check-in confirmed successfully');
      } catch (error) {
        console.error('Error confirming check-in:', error);
        this.notifyError('Failed to confirm check-in');
      }
    },

    formatNumber(value) {
      if (!value) return '0';
      return new Intl.NumberFormat().format(value);
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    formatDateYMD(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString();
    },

    notifySuccess(message) {
      this.$store.dispatch('alerts/success', message);
    },

    notifyError(message) {
      this.$store.dispatch('alerts/error', message);
    }
  },

  watch: {
    dateRange: {
      handler() {
        this.fetchTodayData();
      },
      deep: true
    },

    autoRefresh: {
      handler(val) {
        if (val) {
          this.startAutoRefresh();
        } else {
          this.stopAutoRefresh();
        }
      }
    }
  }
};
</script>

<style scoped>
.today-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.live-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffebee;
  color: #f44336;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
}

.refresh-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.last-updated {
  font-size: 11px;
  color: #999;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.pulse {
  width: 8px;
  height: 8px;
  background: #f44336;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { transform: scale(0.8); opacity: 1; }
  100% { transform: scale(1.2); opacity: 0; }
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.metric-card {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: transform 0.2s;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.metric-icon md-icon {
  font-size: 36px;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.metric-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  padding: 0 16px;
  margin-top: 16px;
}

.pending-count {
  background: #ff9800;
  color: white;
  border-radius: 20px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: bold;
}

.activity-feed {
  max-height: 400px;
  overflow-y: auto;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #e0e0e0;
  transition: background 0.2s;
}

.activity-item:hover {
  background: #f5f5f5;
}

.activity-item.checkin .activity-icon md-icon {
  color: #4caf50;
}

.activity-item.checkout .activity-icon md-icon {
  color: #f44336;
}

.activity-time {
  font-size: 12px;
  color: #666;
  min-width: 60px;
}

.activity-details {
  flex: 1;
}

.activity-title {
  font-weight: 500;
  font-size: 14px;
}

.activity-subtitle {
  font-size: 11px;
  color: #666;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.empty-state md-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state-small {
  text-align: center;
  padding: 20px;
  color: #999;
}

.empty-state-small md-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.schedule-list {
  max-height: 300px;
  overflow-y: auto;
}

.schedule-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.schedule-time {
  font-size: 12px;
  font-weight: 500;
  color: #2196f3;
  min-width: 60px;
}

.schedule-info {
  flex: 1;
}

.schedule-title {
  font-size: 13px;
  font-weight: 500;
}

.schedule-subtitle {
  font-size: 11px;
  color: #666;
}

.schedule-count .badge {
  background: #e3f2fd;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  color: #1976d2;
}

.pending-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
  flex-wrap: wrap;
}

.pending-info {
  flex: 1;
}

.pending-name {
  font-weight: 500;
  font-size: 14px;
}

.pending-detail {
  font-size: 11px;
  color: #666;
}

.pending-time {
  font-size: 10px;
  color: #999;
  margin-top: 4px;
}

.md-button.md-dense {
  margin: 0;
  height: 32px;
  min-height: 32px;
}

.md-button.md-small {
  min-width: 32px;
  height: 32px;
  width: 32px;
  margin: 0;
  padding: 0;
}

.mt-3 {
  margin-top: 16px;
}

/* Responsive */
@media (max-width: 768px) {
  .metrics-grid {
    grid-template-columns: 1fr 1fr;
  }

  .today-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .pending-item {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>